import type {SlDialog, SlSelect, SlOption, SlIcon, SlButton} from "@shoelace-style/shoelace/shoelace";
import {assertString, notNull} from "./assert";
import {setLanguageModalDissmissCookie} from "./setCookies";
import {findLocalizedUrl} from "./i18n/localizedUrls";
import {AutoTranslateStore} from "./i18n/autoTranslateStore";
import {getCookieFirstInitialConsent} from "./common/cookieFirst";

class LanguageSwitcher {
    private dialog: SlDialog;
    private data: LanguageSwitcherData;
    private countrySelectElement: SlSelect;
    private countrySelectIconElement: SlIcon;
    private languageSelectElement: SlSelect;
    private saveButtonElement: SlButton;
    private countryOptions: Map<string, Array<SlOption>> = new Map();
    private autoTranslateStore = new AutoTranslateStore();

    public constructor(dialog: SlDialog, hasAutoTranslationConsent: boolean) {
        this.dialog = dialog;
        this.data = queryLanguageSwitcherData(dialog, hasAutoTranslationConsent);
        this.countrySelectElement = notNull(
            dialog.querySelector<SlSelect>('sl-select[data-preferred-country-select]'),
        );
        this.countrySelectIconElement = notNull(
            this.countrySelectElement.querySelector<SlIcon>(':scope > sl-icon[slot="prefix"]'),
        );
        this.languageSelectElement = notNull(
            dialog.querySelector<SlSelect>('sl-select[data-preferred-language-select]'),
        );
        this.saveButtonElement = notNull(
            dialog.querySelector<SlButton>('sl-button[data-set-language]'),
        );

        this.countrySelectElement.querySelectorAll<SlOption>('sl-option').forEach(o => {
            const country = this.data.countries[o.value] ?? null;
            if (country === null) {
                o.remove();
            }
        });

        for (const countryData of Object.values(this.data.countries)) {
            this.countryOptions.set(
                countryData.code,
                countryData.languages.map(languageData => {
                    const option = document.createElement('sl-option');
                    option.setAttribute('value', languageData.code);
                    option.textContent = languageData.label
                    return option;
                }),
            );
        }

        let currentCountryCode = this.data.currentCountryCode;
        let currentLanguageCode = this.data.currentLanguageCode;

        const preference = this.data.preference;
        const hasValidPreference = (
            preference !== null
            && this.data.countries.hasOwnProperty(preference.countryCode)
            && this.data.countries[preference.countryCode].languages
                .some(l => l.code === preference.languageCode)
        );
        if (hasValidPreference) {
            currentCountryCode = preference.countryCode;
            currentLanguageCode = preference.languageCode;
        }

        this.countrySelectElement.setAttribute('value', currentCountryCode);
        this.onCountryChange(currentLanguageCode);

        this.countrySelectElement.addEventListener('sl-change', () => this.onCountryChange());
        this.dialog.addEventListener('sl-hide', () => this.onDialogHide());
        this.saveButtonElement.addEventListener('click', () => {
            const selectedCountryCode = this.getSelectedCountryCode();
            const selectedLanguageCode = this.getSelectedLanguageCode();

            if (selectedCountryCode === null || selectedLanguageCode === null) {
                // Should not be possible due to UI constraints.
                return;
            }

            setLanguageModalDissmissCookie();

            const language = notNull(this.data.countries[selectedCountryCode].languages
                .find(l => l.code === selectedLanguageCode));

            const urlLanguage = language.isAutoTranslateLanguage ? 'en' : selectedLanguageCode;
            const urlCountry = language.isAutoTranslateLanguage ? 'gb' : selectedCountryCode;
            let redirectUrl = (
                findLocalizedUrl(urlCountry, urlLanguage)
                ?? this.findLocalizedFallbackUrl(urlCountry, urlLanguage)
            );
            if (redirectUrl !== null) {
                redirectUrl.search = window.location.search;
            } else {
                redirectUrl = new URL(window.location.href);
            }

            this.saveButtonElement.setAttribute('disabled', '');
            this.saveButtonElement.setAttribute('loading', '');

            if (language.isAutoTranslateLanguage) {
                this.autoTranslateStore.saveLanguage(selectedLanguageCode);
            } else {
                this.autoTranslateStore.clearLanguage();
            }

            window.location.href = redirectUrl.toString();
        });
    }

    public show(): void {
        this.saveButtonElement.removeAttribute('disabled');
        this.dialog.show();
    }

    private onDialogHide(): void {
        setLanguageModalDissmissCookie();
    }

    private getSelectedCountryCode(): string|null {
        const value = this.countrySelectElement.value;
        assertString(value);
        return value === '' ? null : value;
    }

    private getSelectedLanguageCode(): string|null {
        const value = this.languageSelectElement.value;
        assertString(value);
        return value === '' ? null : value;
    }

    private onCountryChange(selectLanguageCode: string|null = null): void {
        const countryCode = this.getSelectedCountryCode();
        const countryData = countryCode === null ? null : notNull(this.data.countries[countryCode]);

        if (countryData === null) {
            this.countrySelectIconElement.setAttribute('src', '');
        } else {
            this.countrySelectIconElement.setAttribute('src', countryData.iconSrc);
        }

        this.rebuildLanguageOptions(countryData, selectLanguageCode);
    }

    private rebuildLanguageOptions(countryData: LanguageSwitcherCountryData|null, selectLanguageCode: string|null): void {
        this.languageSelectElement.innerHTML = '';
        if (countryData === null) {
            this.languageSelectElement.setAttribute('disabled',  '');
            return;
        }

        const languageOptions = this.countryOptions.get(countryData.code) ?? [];
        this.languageSelectElement.append(...languageOptions);
        this.languageSelectElement.removeAttribute('disabled');
        this.languageSelectElement.setAttribute('value', selectLanguageCode ?? countryData.defaultLanguageCode);
    }

    private findLocalizedFallbackUrl(countryCode2: string, languageCode2: string): URL|null {
        const urlString = this.data.fallbackLanguageUrls[`${languageCode2}-${countryCode2}`] ?? null;
        if (urlString === null) {
            return null;
        }
        return new URL(urlString);
    }
}

type LanguageSwitcherData = {
    currentCountryCode: string;
    currentLanguageCode: string;
    preference: {
        countryCode: string;
        languageCode: string;
    } | null;
    countries: Record<string, LanguageSwitcherCountryData>;
    fallbackLanguageUrls: Record<string, string>,
}

type LanguageSwitcherCountryData = {
    code: string;
    defaultLanguageCode: string,
    iconSrc: string,
    languages: Array<{
        code: string;
        label: string,
        isAutoTranslateLanguage: boolean;
    }>;
};

function queryLanguageSwitcherData(dialog: SlDialog, hasAutoTranslationConsent: boolean): LanguageSwitcherData {
    const switcherDataElement = notNull(
        dialog.querySelector<HTMLScriptElement>(
            'script[type="application/json"][data-preferred-language-dialog-data]',
        ),
    );
    const data = JSON.parse(
        switcherDataElement.textContent,
    ) as LanguageSwitcherData;

    if (hasAutoTranslationConsent) {
        return data;
    }

    // Alle Auto-Translation-Sprachen werden rausgeworfen.
    for (const [countryKey, countryData] of Object.entries(data.countries)) {
        const languagesWithoutAutoTranslation = countryData.languages
            .filter(l => ! l.isAutoTranslateLanguage);

        if (languagesWithoutAutoTranslation.length === countryData.languages.length) {
            continue;
        }

        countryData.languages = languagesWithoutAutoTranslation;

        if (countryData.languages.length === 0) {
            delete data.countries[countryKey];
            continue;
        }

        const stillSupportsDefaultLanguage = countryData.languages
            .some(l => l.code === countryData.defaultLanguageCode);
        if (! stillSupportsDefaultLanguage) {
            countryData.defaultLanguageCode = countryData.languages[0].code;
        }
    }

    return data;
}

async function main() {
    await Promise.all([
        customElements.whenDefined('sl-dialog'),
        customElements.whenDefined('sl-select'),
        customElements.whenDefined('sl-option'),
        customElements.whenDefined('sl-icon'),
        customElements.whenDefined('sl-button'),
    ]);

    const dialog = document.querySelector<SlDialog>('sl-dialog[data-preferred-language-dialog]');
    if (dialog === null) {
        return null;
    }

    /**
     * Logik für Auto-Translation-Consent:
     * Der Language-Switcher funktioniert auch ohne Consent, zeigt
     * dann aber die Auto-Translate-Sprachen nicht an.
     * Der automatische Language-Switcher-Prompt funktioniert erst,
     * nachdem das Cookie-Banner geschlossen wurde. Es gibt komische
     * Browser / Browser-Extensions, die Cookie-Banner nicht anzeigen,
     * daher funktioniert der Language-Switcher generell auch ohne
     * Consent.
     */

    let hasAutoTranslationConsent = true;
    let autoTranslateConsentPromise: Promise<void> = Promise.resolve();

    // Wenn wir noch keinen Cookie-Consent haben, dann müssen wir erst mal warten
    // und schauen, ob wir Consent für Auto-Translation bekommen.
    const awaitAutoTranslationConsentType = dialog.getAttribute('data-await-auto-translation-consent-type');
    if (typeof awaitAutoTranslationConsentType === 'string') {
        hasAutoTranslationConsent = false;
        autoTranslateConsentPromise = getCookieFirstInitialConsent().then(cookieFirstConsent => {
            hasAutoTranslationConsent = cookieFirstConsent[awaitAutoTranslationConsentType] ?? false;
        });
    }

    const shouldAutoPrompt = dialog.hasAttribute('data-preferred-language-dialog-prompt');

    // Language-Switcher wird lazy geladen.
    let languageSwitcher = null;
    let languageSwitcherOpened = false;

    if (shouldAutoPrompt) {
        autoTranslateConsentPromise.then(() => {
            if (languageSwitcherOpened) {
                // Wenn der Language-Switcher schon einmal geöffnet wurde,
                // dann müssen wir ihn nicht noch einmal anzeigen.
                return;
            }
            languageSwitcher ??= new LanguageSwitcher(dialog, hasAutoTranslationConsent);
            languageSwitcher.show();
            languageSwitcherOpened = true;
        });
    }

    const triggerButton = document.querySelector<SlButton>('sl-button[data-open-language-switcher]');
    if (triggerButton !== null) {
        triggerButton.addEventListener('click', () => {
            languageSwitcher ??= new LanguageSwitcher(dialog, hasAutoTranslationConsent);
            languageSwitcher.show();
            languageSwitcherOpened = true;
        });
    }
}

main();
